import { useState } from 'react';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Card, CardBody } from '../../../components/Card';
import { useOverlay } from '../../../overlay';
import { toast } from '../../../components/Toast';
import { api, errorMessage } from '../../../lib/api';
import type { DroneGroup } from '@shared/types';
import { useFleetStore } from '../fleetStore';

const DEFAULT_COLOR = '#2563eb';

/**
 * 分组管理。
 *
 * 分组是「批量统一配置」的抓手，因此放在无人机管理页内，而不是塞进平台设置。
 */
export function GroupManager() {
  const { groups, groupUsage, upsertGroup, removeGroup, setGroups } = useFleetStore();
  const overlay = useOverlay();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  async function reloadGroups() {
    const result = await api.get<{ items: DroneGroup[]; usage: Record<string, number> }>('/api/groups');
    setGroups(result.items, result.usage);
  }

  async function create(event: ReactMouseEvent<HTMLButtonElement>) {
    const name = await overlay.prompt({
      placementKey: 'group-create',
      trigger: event.currentTarget,
      title: '新建分组',
      input: { label: '分组名称', placeholder: '例如：东区巡检', maxLength: 30 },
    });
    if (name === null || name.trim() === '') return;
    try {
      const created = await api.post<DroneGroup>('/api/groups', {
        name: name.trim(),
        description: '',
        color: DEFAULT_COLOR,
      });
      upsertGroup(created);
      toast.ok('分组已创建');
    } catch (error) {
      toast.critical(errorMessage(error));
    }
  }

  async function rename(event: ReactMouseEvent<HTMLButtonElement>, group: DroneGroup) {
    const name = await overlay.prompt({
      placementKey: `group-rename:${group.id}`,
      trigger: event.currentTarget,
      title: `重命名分组「${group.name}」`,
      input: { label: '新的分组名称', defaultValue: group.name, maxLength: 30 },
    });
    if (name === null || name.trim() === '' || name.trim() === group.name) return;
    try {
      const updated = await api.patch<DroneGroup>(`/api/groups/${group.id}`, { name: name.trim() });
      upsertGroup(updated);
      toast.ok('分组已重命名');
    } catch (error) {
      toast.critical(errorMessage(error));
    }
  }

  async function recolor(event: ReactMouseEvent<HTMLButtonElement>, group: DroneGroup) {
    const color = await overlay.prompt({
      placementKey: `group-color:${group.id}`,
      trigger: event.currentTarget,
      title: `设置「${group.name}」的颜色`,
      input: { label: '十六进制色值', defaultValue: group.color, placeholder: '#2563eb', maxLength: 7 },
    });
    if (color === null) return;
    const value = color.trim().toLowerCase();
    if (!/^#[0-9a-f]{6}$/.test(value)) {
      toast.caution('颜色必须是 #RRGGBB 形式');
      return;
    }
    try {
      const updated = await api.patch<DroneGroup>(`/api/groups/${group.id}`, { color: value });
      upsertGroup(updated);
      toast.ok('分组颜色已更新');
    } catch (error) {
      toast.critical(errorMessage(error));
    }
  }

  async function remove(event: ReactMouseEvent<HTMLButtonElement>, group: DroneGroup) {
    const used = groupUsage[group.id] ?? 0;
    const confirmed = await overlay.confirm({
      placementKey: `group-delete:${group.id}`,
      trigger: event.currentTarget,
      tone: 'danger',
      title: `删除分组「${group.name}」？`,
      description:
        used > 0
          ? `该分组下仍有 ${used} 台无人机，需要先改分组或移除设备后才能删除。`
          : '删除后不影响无人机本身，仅解除分组关系。',
      confirmText: '删除分组',
    });
    if (!confirmed) return;

    setBusyId(group.id);
    try {
      await api.delete(`/api/groups/${group.id}`);
      removeGroup(group.id);
      await reloadGroups().catch(() => undefined);
      toast.ok('分组已删除');
    } catch (error) {
      toast.critical(errorMessage(error));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card>
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-3.5 py-2.5">
        <span className="text-sm font-semibold">分组管理</span>
        <span className="text-[11px] text-fg-subtle">分组用于批量配置与总览筛选</span>
        <div className="ml-auto flex items-center gap-1.5">
          <Button size="sm" variant="ghost" onClick={() => setOpen((value) => !value)}>
            {open ? '收起' : '展开'}
          </Button>
          <Button size="sm" onClick={(event) => void create(event)}>
            新建分组
          </Button>
        </div>
      </div>

      {open ? (
        <CardBody className="flex flex-col gap-2">
          {groups.length === 0 ? (
            <p className="text-xs text-fg-subtle">还没有分组。建议按作业区域或机型分组，便于统一配置。</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {groups.map((group) => (
                <li key={group.id} className="flex flex-wrap items-center gap-2 py-2">
                  <span
                    aria-hidden
                    className="size-3.5 shrink-0 rounded-sm border border-line"
                    style={{ background: group.color }}
                  />
                  <span className="text-sm font-medium text-fg">{group.name}</span>
                  <Badge tone="neutral">{groupUsage[group.id] ?? 0} 台</Badge>
                  {group.description ? (
                    <span className="truncate text-[11px] text-fg-subtle">{group.description}</span>
                  ) : null}
                  <div className="ml-auto flex items-center gap-1.5">
                    <Button size="sm" variant="ghost" onClick={(event) => void recolor(event, group)}>
                      颜色
                    </Button>
                    <Button size="sm" variant="ghost" onClick={(event) => void rename(event, group)}>
                      重命名
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      loading={busyId === group.id}
                      onClick={(event) => void remove(event, group)}
                    >
                      删除
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      ) : null}
    </Card>
  );
}
